import { useState } from "react";
import { PrivacyConsent, GuardianConsent } from "../../components/site/Consent";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Info } from "lucide-react";
import { useLang } from "../../lib/i18n";
import { useAuth } from "../../lib/auth";
import { api, errMsg } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Field, SailorFields, SportFields, isMinor } from "../../components/site/ProfileFields";
import { AuthShell } from "./Login";
import { GEN } from "../../components/site/Img";
import { WhoStep, ChildrenStep } from "./RegisterChildren";

export default function Register() {
  const { t } = useLang();
  const { signIn } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const eventId = sp.get("event");
  const [f, setF] = useState({ classes: [], boats: [], interest_classes: [] });
  const [who, setWho] = useState("");
  const [kids, setKids] = useState([{ classes: [] }]);
  const [acc, setAcc] = useState({ email: "", password: "" });
  const [photo, setPhoto] = useState(null);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const steps = ["parent", "who", ...(who === "children" ? ["children"] : ["sport"]), "access"];
  const cur = steps[step];
  const anyMinor = who === "children" && kids.some((k) => isMinor(k.birth_date));

  const checkKids = async () => {
    const keys = kids.map((k) => `${(k.first_name || "").trim().toLowerCase()}|${(k.last_name || "").trim().toLowerCase()}|${k.birth_date}`);
    if (new Set(keys).size !== keys.length) {
      toast.error(t("reg_child_twice"));
      return false;
    }
    for (const k of kids) {
      const { data } = await api.post("/auth/register/check-child", k);
      if (data.exists) {
        toast.error(`${k.first_name} ${k.last_name}: ${t("reg_child_exists")}`);
        return false;
      }
    }
    return true;
  };

  const create = async () => {
    const { data } = await api.post("/auth/register", { ...f, ...acc, participates: who, children: who === "children" ? kids : [],
      guardian_consent: anyMinor && !!acc.guardian_consent, language: localStorage.getItem("aa_lang") || "en" });
    signIn(data);
    if (photo && who === "self") {
      const fd = new FormData();
      fd.append("file", photo);
      const up = await api.post("/files/upload", fd);
      const self = data.user.participants.find((p) => p.relation === "self");
      if (self) await api.put(`/me/participants/${self.id}`, { photo_path: up.data.path });
    }
    toast.success(t("welcome"));
    nav(eventId ? `/clinics/${eventId}` : "/account");
  };

  const next = async (e) => {
    e.preventDefault();
    if (cur === "parent" && isMinor(f.birth_date)) return toast.error(t("reg_parent_adult"));
    if (cur === "who" && !who) return toast.error(t("reg_who_pick"));
    setBusy(true);
    try {
      if (cur === "children" && !(await checkKids())) return;
      if (step < steps.length - 1) return setStep(step + 1);
      await create();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell imgKey="register_side" fallback={GEN.optimist}>
      <form onSubmit={next} className="space-y-6" data-testid="register-form">
        <h1 className="font-display text-4xl font-extrabold text-[#0A2540]">{t("register")}</h1>
        <div className="flex gap-2">
          {steps.map((s, i) => (
            <div key={s} className="flex-1">
              <div className={`h-1.5 rounded-full ${i <= step ? "bg-[#FF5722]" : "bg-slate-200"}`} />
              <p className={`mt-2 text-xs font-semibold ${i === step ? "text-[#0A2540]" : "text-slate-400"}`}>{t(`step_${s}`)}</p>
            </div>
          ))}
        </div>
        {cur === "parent" && (
          <>
            <div className="flex items-start gap-3 rounded-2xl bg-cyan-50 p-4 text-sm text-[#0A2540]" data-testid="register-parent-note"><Info size={20} className="shrink-0 text-[#00A8B5]" />{t("reg_parent_note")}</div>
            <SailorFields f={f} setF={setF} holder />
          </>
        )}
        {cur === "who" && <WhoStep who={who} setWho={setWho} />}
        {cur === "children" && <ChildrenStep kids={kids} setKids={setKids} />}
        {cur === "sport" && (
          <>
            <SportFields f={f} setF={setF} />
            <Field label={t("photo")}><input type="file" accept="image/*" onChange={(e) => setPhoto(e.target.files?.[0] || null)} data-testid="register-photo-input" className="text-sm" /></Field>
          </>
        )}
        {cur === "access" && (
          <div className="space-y-4">
            <Field label={t("email")}><Input type="email" required value={acc.email} onChange={(e) => setAcc({ ...acc, email: e.target.value })} className="h-12 rounded-xl" data-testid="register-email-input" /></Field>
            <Field label={t("password")}><Input type="password" required minLength={6} value={acc.password} onChange={(e) => setAcc({ ...acc, password: e.target.value })} className="h-12 rounded-xl" data-testid="register-password-input" /></Field>
            <PrivacyConsent checked={acc.privacy_consent} onChange={(v) => setAcc({ ...acc, privacy_consent: v })} />
            {anyMinor && <GuardianConsent checked={acc.guardian_consent} onChange={(v) => setAcc({ ...acc, guardian_consent: v })} />}
          </div>
        )}
        <div className="flex items-center justify-between">
          {step > 0 ? <button type="button" onClick={() => setStep(step - 1)} className="text-sm font-semibold text-slate-500" data-testid="register-back">{t("back")}</button> : <span />}
          <button disabled={busy} className="btn-buoy disabled:opacity-60" data-testid="register-next">{step === steps.length - 1 ? t("create_account") : t("next")}</button>
        </div>
        <p className="text-sm text-slate-500">{t("have_account")} <Link to="/login" className="font-semibold text-[#FF5722]">{t("login")}</Link></p>
      </form>
    </AuthShell>
  );
}

